<template>
  <v-layout
    v-if="!isSheetMeetingLoading"
    id="content-area"
    column
    py-3
    fill-height
  >
    <div
      v-for="(section, index) in meetingComponents"
      :key="index"
    >
      <div v-if="handleSectionVisibility(section)">
        <component
          :is="section.component"
          v-view="viewHandler"
          class="pt-3 mt-4"
          :read-only="readOnly"
          :is-current-edition="isCurrentEdition"
        />
        <v-divider
          v-if="index !== 0 && index < meetingComponents.length - 1"
          class="mt-5"
        />
      </div>
    </div>

    <sheetEndButtons
      :sheet-type="'meeting'"
      :family-id="$route.params.familyId"
      to="sheet-one-reflection"
      next-section-name="Refleksji"
      :read-only="readOnly"
      :can-do-audit-in-area="canDoAuditInArea"
      :can-do-audit-on-national="canDoAuditOnNational"
      :national-correcting-permission="hasPermissionsForNationalCorrecting"
      :scroll-button="true"
    />
    <leaveConfirmDialog ref="confirm" />
    <FamilyAudit
      v-if="showAuditCard"
      :can-add-comment="canAddComment"
      :family-id="$route.params.familyId"
      :sheet-type="'meeting'"
      :comment-level="commentLevel"
      :has-permissions-to-add-comment="hasPermissionsToAddComment"
    />
  </v-layout>
  <PageLoader v-else />
</template>

<script>
import AppLogo from '@/components/widgets/AppLogo';
import familyGuide from '@/components/sheet/sheetOne/questions/FamilyGuide';
import fillInstruction from '@/components/sheet/sheetOne/questions/fillInstruction';
import previousEditions from '@/components/sheet/sheetOne/questions/previousEditions';
import SheetEndButtons from '@/components/widgets/sheet/sheetEndButtons';
import FamilyHistory from '@/components/sheet/sheetOne/questions/FamilyHistory';
import FamilyResources from '@/components/sheet/sheetOne/questions/FamilyResources';
import QuestionsBarriers from '@/components/sheet/sheetOne/questions/QuestionsBarriers';
import SocialSeparation from '@/components/sheet/sheetOne/questions/SocialSeparation';
import MaintenanceExpenses from '@/components/sheet/sheetOne/questions/maintenanceExpenses';
import QuestionsPossessions from '@/components/sheet/sheetOne/questions/QuestionsPossessions';
import ExpensesForMedicine from '@/components/sheet/sheetOne/questions/ExpensesForMedicine';
import DoctorsPack from '@/components/sheet/sheetOne/questions/DoctorsPack';
import QuestionsCredits from '@/components/sheet/sheetOne/questions/QuestionsCredits';
import LawyersPack from '@/components/sheet/sheetOne/questions/lawyersPack';
import CashReceipts from '@/components/sheet/sheetOne/questions/CashReceipts';
import QuestionsJobless from '@/components/sheet/sheetOne/questions/QuestionsJobless';
import SheetOneConsents from '@/components/sheet/sheetOne/questions/SheetOneConsents';
import SheetOneVolunteers from '@/components/sheet/sheetOne/questions/SheetOneVolunteers';
import leaveConfirmDialog from '@/components/widgets/LeaveConfirmDialog';
import FamilyAudit from '@/components/audit/FamilyAudit';

import defaultTableOfC from '@/constants/sheet/sheetOne';
import sheetBeforeLeave from '@/mixins/sheetBeforeLeave';
import auditPermissionsAndStatuses from '@/mixins/auditPermissionsAndStatuses';
import isFamilyChecked from '@/mixins/sheet/isFamilyChecked';

import permissions from '@/constants/permissions';
import { mapGetters } from 'vuex';
import PageLoader from '@/components/widgets/PageLoader';
import postMeetingConsent from '@/constants/sheet/postMeetingConsent';

export default {
    name: 'SheetMeeting',
    $_veeValidate: {
        validator: 'new' // give me my own validator scope.
    },
    components: {
        PageLoader,
        LawyersPack,
        MaintenanceExpenses,
        SheetEndButtons,
        previousEditions,
        familyGuide,
        fillInstruction,
        AppLogo,
        FamilyHistory,
        FamilyResources,
        QuestionsBarriers,
        SocialSeparation,
        QuestionsPossessions,
        ExpensesForMedicine,
        DoctorsPack,
        QuestionsCredits,
        CashReceipts,
        QuestionsJobless,
        SheetOneConsents,
        SheetOneVolunteers,
        leaveConfirmDialog,
        FamilyAudit
    },
    mixins: [sheetBeforeLeave, auditPermissionsAndStatuses, isFamilyChecked, postMeetingConsent],
    beforeRouteEnter (to, from, next) {
        next((vm) => {
            vm.isSheetMeetingLoading = true;
            vm.$store.dispatch('sheet/getSheetPermissions', vm.$route.params.familyId);
            vm.$store
                .dispatch('sheet/loadSheet', { familyId: vm.$route.params.familyId, sheetType: 'meeting' })
                .finally(() => {
                    vm.isSheetMeetingLoading = false;
                    if (
                        vm &&
            vm.getJoblessQuestions &&
            Object.prototype.hasOwnProperty.call(vm.getJoblessQuestions, 'about_sixty')
                    ) {
                        vm.handleTableOfC();
                    }
                    vm.hideSections();
                });
            vm.$store.commit('sheet/setCurrentRouteName', to.name);
            vm.$store.commit('sheet/setCurrentSelectorName', 'instruction');
        });
    },
    data () {
        return {
            permissions,
            defaultTableOfC,
            isSheetMeetingLoading: true
        };
    },
    computed: {
        ...mapGetters('sheet', ['getJoblessQuestions', 'getDoctorsPackQuestions']),
        showDoctorsPack () {
            return !this.$store.getters['sheet/getHiddenSelectorsList'].includes('doctorsPack');
        },
        showLawyersPack () {
            return !this.$store.getters['sheet/getHiddenSelectorsList'].includes('lawyersPack');
        },
        sheetPermissions () {
            return this.$store.getters['sheet/getSheetPermissions'];
        },
        isCurrentEdition () {
            return this.$store.getters['family/isCurrentEdition'];
        },
        readOnly () {
            if (
                !this.canEditSheets ||
        (this.isFamilyVerifiedOnNational && !this.hasPermissionsForNationalCorrecting) ||
        !this.isCurrentEdition ||
        !this.isFamilyChecked
            ) {
                return true;
            }
            return !this.sheetPermissions.meeting;
        },
        getTableOfContentsList () {
            return this.$store.getters['sheet/getTableOfContentsList'];
        },
        meetingComponents () {
            return (
                this.getTableOfContentsList &&
        this.getTableOfContentsList[0] &&
        this.getTableOfContentsList[0].filter(
            (el) => el && !Object.prototype.hasOwnProperty.call(el, 'header')
        )
            );
        }
    },
    methods: {
        viewHandler (e) {
            if (e.scrollPercent < 1) {
                if (e.target.rect.top <= 50) {
                    this.$store.commit('sheet/setCurrentSelectorName', e.target.element.id);
                }
            } else {
                if (e.percentTop <= 1) {
                    this.$store.commit('sheet/setCurrentSelectorName', e.target.element.id);
                }
            }
        },
        handleTableOfC () {
            const result = [...defaultTableOfC];
            const seniorPack = result[0].find((el) => el && el.selector === 'seniorPack');
            const seniorPackIndex = result[0].findIndex((el) => el && el.selector === 'seniorPack');
            result[0].splice(seniorPackIndex, 1);
            result[0].splice(
                result[0].findIndex((el) => el && el.selector === 'consents'),
                0,
                seniorPack
            );
            this.$store.dispatch('sheet/addTableOfContentsList', result);
        },
        hideSections () {
            this.$store.getters['sheet/getTableOfContentsList'].forEach((el) => {
                el.forEach((item) => {
                    if (item && item.variable === true) {
                        this.$store.commit('sheet/addHiddenSelector', item.selector);
                    }
                    if (item && item.selector === 'doctorsPack') {
                        item.name = this.getDoctorsPackQuestions?.label;
                    }
                });
            });
        },
        handleSectionVisibility (section) {
            switch (section.selector) {
            case 'doctorsPack':
                return this.showDoctorsPack;
            case 'lawyersPack':
                return this.showLawyersPack;
            default:
                return true;
            }
        }
    }
};
</script>

<style scoped></style>
